"use client";

import { useState, useMemo, useEffect } from 'react';
import { carServiceSuggestions, type CarServiceSuggestionsOutput } from '@/ai/flows/car-service-suggestions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Wrench,
  Warehouse,
  CircleCheck,
  Search,
  Star,
  MapPin,
  MessageCircle,
  Phone,
  MessageSquare,
  Sparkles,
  Loader2,
  CircleAlert
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import Link from 'next/link';
import { useCountry, COUNTRIES } from '@/context/CountryContext';
import { workshopsApi } from '@/lib/api';

const CATEGORIES = ['الكل', 'المحرك', 'كهرباء', 'الفرامل', 'تكييف', 'سمكرة'];

interface Workshop {
  id: number | string;
  name?: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  location?: string;
  phone?: string;
  image?: string;
  category?: string;
  rating?: string | number;
  workshop_store?: { store_name: string; store_name_ar: string };
}

export default function MaintenanceClient() {
  const { selectedCountryCode } = useCountry();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [isWorkshopsLoading, setIsWorkshopsLoading] = useState(true);
  const [problem, setProblem] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState<CarServiceSuggestionsOutput | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('الكل');

  useEffect(() => {
    setIsWorkshopsLoading(true);
    workshopsApi.getWorkshops({ country_code: selectedCountryCode })
      .then((data) => {
        // Handle paginated or plain array response
        setWorkshops(Array.isArray(data) ? data : data.results || []);
      })
      .catch((err) => {
        console.error('Failed to load workshops from Django API:', err);
        setWorkshops([]);
      })
      .finally(() => setIsWorkshopsLoading(false));
  }, [selectedCountryCode]);

  const handleGetSuggestions = async () => {
    if (!problem.trim()) return;
    setLoading(true);
    try {
      const output = await carServiceSuggestions({ problemDescription: problem });
      setAiResult(output);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkshops = useMemo(() => {
    return workshops.filter(w => {
      const name = w.name_ar || w.name || '';
      const desc = w.description_ar || w.description || '';
      const cat = w.category || '';
      const matchesSearch = name.includes(searchQuery) || desc.includes(searchQuery);
      const matchesCategory = selectedCategory === 'الكل' || cat.includes(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [workshops, searchQuery, selectedCategory]);

  const workshopName = (w: Workshop) => w.name_ar || w.name || w.workshop_store?.store_name_ar || 'ورشة';
  const workshopDesc = (w: Workshop) => w.description_ar || w.description || 'لا يوجد وصف متوفر لهذه الورشة.';
  const workshopCat = (w: Workshop) => w.category || 'عام';

  return (
    <div className="space-y-6 pb-24">
      <section className="bg-white p-6 rounded-[2.5rem] shadow-sm border space-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 p-3 rounded-2xl text-primary">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold font-headline">المساعد الذكي للصيانة</h2>
            <p className="text-sm text-muted-foreground">أدخل المشكلة وسنقترح عليك الحلول والورش</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="مثال: صوت طقطقة في المحرك..."
            className="rounded-2xl h-14 bg-slate-50 border-none shadow-inner"
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
          />
          <Button
            className="h-14 rounded-2xl px-8 bg-primary shadow-lg shadow-primary/20"
            disabled={loading || !problem.trim()}
            onClick={handleGetSuggestions}
          >
            {loading ? <Loader2 className="animate-spin h-5 w-5" /> : <Search className="h-5 w-5" />}
          </Button>
        </div>

        {aiResult && (
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2 duration-300">
            <div className="bg-blue-50/50 p-4 rounded-3xl border border-blue-100">
              <h3 className="text-sm font-bold text-secondary mb-3 flex items-center gap-2">
                <CircleCheck className="h-4 w-4" /> الخدمات المقترحة
              </h3>
              <div className="flex flex-wrap gap-2">
                {aiResult.suggestedServices.map((s, i) => (
                  <Badge key={i} className="bg-white text-slate-700 hover:bg-white border-none shadow-sm rounded-xl px-3 py-1">
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="bg-orange-50/50 p-4 rounded-3xl border border-orange-100">
              <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
                <Warehouse className="h-4 w-4" /> ورش متخصصة
              </h3>
              <div className="flex flex-wrap gap-2">
                {aiResult.suggestedWorkshops.map((w, i) => (
                  <Badge key={i} className="bg-white text-slate-700 hover:bg-white border-none shadow-sm rounded-xl px-3 py-1">
                    {w}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-4">
          <div className="relative group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 h-5 w-5 transition-colors group-focus-within:text-primary" />
            <Input
              className="pr-12 rounded-2xl h-14 bg-white border-none shadow-sm text-sm"
              placeholder="ابحث عن اسم ورشة أو خدمة معينة..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {CATEGORIES.map(cat => (
              <Badge
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-6 py-2 rounded-full cursor-pointer whitespace-nowrap text-xs font-bold transition-all ${selectedCategory === cat ? 'bg-primary text-white' : 'bg-white text-slate-400 hover:bg-slate-50'
                  }`}
              >
                {cat}
              </Badge>
            ))}
          </div>
        </div>

        {isWorkshopsLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="mt-4 text-slate-500 font-bold">جاري تحميل الورش...</p>
          </div>
        ) : filteredWorkshops.length === 0 ? (
          <div className="bg-white p-12 rounded-[2.5rem] border-none shadow-sm text-center space-y-4">
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
              <CircleAlert className="h-10 w-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">لا توجد ورش متوفرة</h3>
            <p className="text-sm text-slate-400 max-w-xs mx-auto">
              عذراً، لا توجد ورش متوفرة في {COUNTRIES.find(c => c.code === selectedCountryCode)?.name} حالياً. جرب اختيار دولة أخرى أو تغيير معايير البحث.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredWorkshops.map((workshop) => (
              <Dialog key={workshop.id}>
                <DialogTrigger asChild>
                  <Card className="rounded-[2rem] border-none shadow-sm bg-white overflow-hidden hover:shadow-md transition-all cursor-pointer group">
                    <div className="flex p-4 gap-4">
                      <div className="w-24 h-24 rounded-2xl overflow-hidden shrink-0 border">
                        <img src={workshop.image || 'https://picsum.photos/seed/workshop/200/200'} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className="font-bold text-slate-800 truncate">{workshopName(workshop)}</h3>
                          <div className="flex items-center gap-1 text-yellow-500">
                            <Star className="h-3 w-3 fill-current" />
                            <span className="text-[10px] font-bold">{workshop.rating || '4.5'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-slate-400 text-[10px] mb-2">
                          <MapPin className="h-3 w-3" />
                          {workshop.location || 'متوفر'}
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="secondary" className="bg-blue-50 text-blue-600 border-none text-[9px] px-2">
                            {workshopCat(workshop)}
                          </Badge>
                          <Badge variant="secondary" className="bg-green-50 text-green-600 border-none text-[9px] px-2">
                            متاح الآن
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                </DialogTrigger>
                <DialogContent className="rounded-[2.5rem] p-0 overflow-hidden border-none max-w-md">
                  <div className="h-48 relative">
                    <img src={workshop.image || 'https://picsum.photos/seed/workshop/600/400'} className="w-full h-full object-cover" alt="" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 right-4 text-white">
                      <h2 className="text-xl font-bold">{workshopName(workshop)}</h2>
                      <div className="flex items-center gap-1 text-xs opacity-80">
                        <MapPin className="h-3 w-3" /> {workshop.location || 'متوفر'}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="space-y-2">
                      <h4 className="font-bold text-sm text-slate-800">عن الورشة</h4>
                      <p className="text-xs text-slate-500 leading-relaxed">{workshopDesc(workshop)}</p>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <Star className="h-4 w-4 text-yellow-500 mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">{workshop.rating || '4.5'} تقييم</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <CircleCheck className="h-4 w-4 text-green-500 mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">معتمد</span>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-2xl text-center">
                        <Wrench className="h-4 w-4 text-primary mx-auto mb-1" />
                        <span className="block text-[10px] font-bold">{workshopCat(workshop)}</span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-bold text-sm text-slate-800">تواصل مباشر</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <Button className="rounded-2xl h-12 bg-primary flex gap-2" asChild>
                          <Link href={`/chat`}>
                            <MessageSquare className="h-4 w-4" /> الدردشة
                          </Link>
                        </Button>
                        <Button variant="outline" className="rounded-2xl h-12 border-green-500 text-green-600 hover:bg-green-50 flex gap-2" asChild>
                          <a href={`https://wa.me/${workshop.phone || ''}`} target="_blank">
                            <MessageCircle className="h-4 w-4" /> واتساب
                          </a>
                        </Button>
                      </div>
                      <Button variant="outline" className="w-full rounded-2xl h-12 flex gap-2 border-slate-200" asChild>
                        <a href={`tel:${workshop.phone || ''}`}>
                          <Phone className="h-4 w-4 text-secondary" /> مكالمة هاتفية
                        </a>
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
